import { useCallback, useState } from "react";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { processImage } from "@/lib/utils/image";

const MAX_PHOTOS = 6;

interface PhotoFile {
  id: string;
  file: File;
  preview: string;
}

interface PhotoUploadAreaProps {
  photos: PhotoFile[];
  onPhotosChange: (photos: PhotoFile[]) => void;
  maxPhotos?: number;
  className?: string;
}

export function PhotoUploadArea({
  photos,
  onPhotosChange,
  maxPhotos = MAX_PHOTOS,
  className,
}: PhotoUploadAreaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files) return;

      const remainingSlots = maxPhotos - photos.length;
      const newFiles = Array.from(files).slice(0, remainingSlots);

      setIsConverting(true);
      try {
        const processedPhotos: PhotoFile[] = await Promise.all(
          newFiles.map(async (file) => {
            const processedFile = await processImage(file);
            return {
              id: crypto.randomUUID(),
              file: processedFile,
              preview: URL.createObjectURL(processedFile),
            };
          })
        );

        onPhotosChange([...photos, ...processedPhotos]);
      } catch (error) {
        console.error("Failed to process photos:", error);
      } finally {
        setIsConverting(false);
      }
    },
    [photos, maxPhotos, onPhotosChange]
  );

  const handleRemovePhoto = useCallback(
    (photoId: string) => {
      const photoToRemove = photos.find((p) => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.preview);
      }
      onPhotosChange(photos.filter((p) => p.id !== photoId));
    },
    [photos, onPhotosChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  // Create array of 6 slots
  const slots = Array.from({ length: maxPhotos }, (_, i) => {
    return photos[i] || null;
  });

  return (
    <div
      className={cn("space-y-3", className)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Photo Grid - Always shows 6 slots */}
      <div className="grid grid-cols-3 gap-3">
        {slots.map((photo, index) =>
          photo ? (
            /* Filled Slot */
            <div
              key={photo.id}
              className="relative aspect-square overflow-hidden rounded-2xl"
            >
              <img
                src={photo.preview}
                alt="Upload preview"
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemovePhoto(photo.id)}
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm transition-colors hover:bg-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            /* Empty Slot */
            <label
              key={`empty-${index}`}
              className={cn(
                "flex aspect-square cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed transition-colors",
                isDragging
                  ? "border-[#8B8B5E] bg-[#F5F5DC]/30"
                  : "border-[#C4C4A0] hover:border-[#8B8B5E] hover:bg-[#F5F5DC]/20",
                isConverting && "pointer-events-none opacity-50"
              )}
            >
              <Plus className="h-6 w-6 text-gray-800" />
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic,image/heif"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
                disabled={isConverting}
              />
            </label>
          )
        )}
      </div>

      {/* File Info */}
      <p className="text-sm text-gray-500">
        {isConverting ? "Processing..." : "Supports JPG, PNG, HEIC · 25 MB max"}
      </p>
    </div>
  );
}

export type { PhotoFile };
